/**
 * What the state IS. Not what it will become.
 *
 * Addressed to the client, because they are the reader with the least context
 * and the most at stake. An attorney or an operator reading the same sentence
 * loses nothing by its being plain.
 */
export const CASE_STATUS_MEANING = {
    DRAFT: "The request exists but has not been submitted, so nothing has been done with it and " +
        "nobody has been asked to look at it.",
    SUBMITTED: "The details you gave are enough to go looking for the parcel at a land registry. That is " +
        "the whole of what it means — no registry has been identified yet, and nothing about the " +
        "land itself has been checked.",
    VALIDATING: "The request is with TitleVerify's own staff. The platform could not work out which land " +
        "registry has jurisdiction over the parcel from its registry-office table, so a person has " +
        "to look at it. This is our gap to close, not something you left out.",
    NEEDS_INFO: "The request is waiting on one more detail from you before the search can be started. " +
        "Everything you have already given us is held against the request and nothing was lost.",
    ROUTED: "The land registry with jurisdiction over the parcel has been worked out and written to the " +
        "request. No attorney has been put on it yet.",
    ASSIGNED: "An attorney-at-law whose enrolment was checked against a recorded source has been assigned " +
        "to this request and can now see it and the documents held on it.",
    SEARCH_IN_PROGRESS: "The search at the land registry has been started. The work happens at the registry itself, " +
        "not in this app, so this state is a record that it is under way and not a live view of it.",
    UNDER_REVIEW: "The attorney is reading what the search turned up and forming their own opinion on it. " +
        "This says nothing about what they have found — that is theirs to state, and only they may.",
    OPINION_READY: "The attorney has finished their opinion. It has not been packaged or delivered to you yet.",
    PACKAGED: "The attorney's opinion has been prepared for delivery. It has not been delivered yet.",
    /*
     * States what the RECORD says, not that a delivery happened — because no code
     * delivers anything. MD6 owns packaging and delivery and is not built: nothing
     * in the API sends on any channel, so this status is set by a person and means
     * they marked it, not that the platform handed the opinion over and observed it
     * arrive. Its two neighbours above already say "it has not been delivered yet";
     * this one asserted the opposite on the strength of a status field alone.
     *
     * Reachable by the client — ClientCases renders CaseProgress — so this was the
     * fifth false claim of its kind, and it survived the sweep that caught the other
     * four because that sweep reads apps/web and this string lives in shared.
     */
    DELIVERED: "This request is marked as delivered. That is a record made by someone here, not something " +
        "the platform carried out or watched arrive — if you have not received the opinion, tell us.",
    CLOSED: "No further work is being done on this request. A request can be closed for several reasons " +
        "and this state does not record which — ask us if you need to know.",
    RETENTION_EXPIRED: "This request reached the end of the period its documents were held for. The record of the " +
        "request remains; whether a particular document is still held is shown against that document.",
};
/**
 * Whose move it is, and what that move is.
 *
 * The client-facing value of this is almost entirely in the `waitingOn` field.
 * "Waiting on us" and "waiting on you" are the two things a person with a
 * stalled request most needs distinguished, and the platform genuinely knows
 * which — it is a property of the state, not a guess.
 */
export const CASE_NEXT_STEP = {
    DRAFT: {
        waitingOn: "client",
        next: "Submitting the request is what starts anything happening.",
    },
    SUBMITTED: {
        waitingOn: "titleverify",
        // Deliberately says a PERSON. Routing is an operator action, not a job.
        next: "Someone at TitleVerify works out which land registry has jurisdiction over the parcel. " +
            "This is a step a person here takes; it does not happen on its own. Nothing is needed " +
            "from you.",
    },
    VALIDATING: {
        waitingOn: "titleverify",
        next: "Someone at TitleVerify has to resolve the jurisdiction before the request can go further. " +
            "Nothing is being asked of you.",
    },
    NEEDS_INFO: {
        waitingOn: "client",
        next: "You add the outstanding detail, which is listed on the request itself. Once it is in, " +
            "the request is reassessed and carries on from where it was.",
    },
    ROUTED: {
        waitingOn: "titleverify",
        next: "Someone at TitleVerify assigns an attorney-at-law to the request. You do not choose the " +
            "attorney and neither does the platform advertise one to you — where a request goes is " +
            "decided by registry jurisdiction and verified standing.",
    },
    ASSIGNED: {
        waitingOn: "attorney",
        next: "The attorney does the search at the land registry. If they need to speak to you, they " +
            "can propose a meeting on this request.",
    },
    SEARCH_IN_PROGRESS: {
        waitingOn: "attorney",
        next: "The attorney continues the search and then reviews what it produced.",
    },
    UNDER_REVIEW: {
        waitingOn: "attorney",
        next: "The attorney forms their opinion. They may also send the search back for more records " +
            "if the chain needs them — that is ordinary work, not a setback.",
    },
    OPINION_READY: {
        // NOT "waiting on TitleVerify". This used to say "TitleVerify packages the
        // attorney's opinion for delivery to you", which described a step that does
        // not exist — no packaging and no delivery channel are built — and told a
        // client to wait for something nobody was going to do.
        waitingOn: "nobody",
        next: "The attorney has given their opinion and you can read it on this request. Nothing further " +
            "is owed on it here. If you have a question about what it says, it is the attorney who can " +
            "answer that — it is their opinion, not ours.",
    },
    PACKAGED: {
        waitingOn: "titleverify",
        next: "TitleVerify delivers the opinion to you.",
    },
    DELIVERED: {
        waitingOn: "nobody",
        next: "Nothing further is owed on this request. The opinion is the attorney's — if you have a " +
            "question about what it says, it is the attorney who can answer it.",
    },
    CLOSED: {
        waitingOn: "nobody",
        next: "Nothing further is being done. Opening a new request is always possible.",
    },
    RETENTION_EXPIRED: {
        waitingOn: "nobody",
        next: "Nothing further is being done on this request.",
    },
};
/**
 * Why there is no date anywhere near a case status, said plainly.
 *
 * Without this the absence of an estimate reads as a missing feature, and a
 * reader supplies their own expectation — which is worse than a stated unknown.
 * With it, the absence is a position the platform has taken and can defend.
 */
export const NO_ESTIMATE_NOTE = "TitleVerify does not estimate how long a step will take, and there is no date or progress " +
    "figure anywhere on this page. What a search at a land registry takes depends on that " +
    "registry and on the deed chain behind the parcel, and neither is something this platform " +
    "can know in advance. A guess presented as a schedule would be worth less to you than the " +
    "plain statement of whose move it is.";
/**
 * Nothing arrives. Opening the page is the only way to learn anything.
 *
 * The client is told this once — in a <small> inside the "How we may contact
 * you" fieldset of the intake form, before their first submit — and then never
 * again. The status panel explains at length why there is no DATE, and never
 * says the corresponding thing about notification, so a client who ticked the
 * email consent box waits for a message instead of checking. There is no
 * notifier of any kind in apps/api/src: no mailer, no WhatsApp client, no push.
 *
 * Sits beside NO_ESTIMATE_NOTE rather than being appended to it because the two
 * are different claims — one is about time, one is about arrival — and a
 * reviewing attorney should be able to weigh them separately.
 *
 * NARROWED, NOT DELETED, when email delivery shipped. The previous version said
 * "Nothing will reach you when this changes — no email, no message, no alert",
 * which was true for this platform's whole life and became false the day the
 * mailer landed. Deleting it would have removed a disclosure the reader is
 * entitled to: almost nothing here notifies, and a client who assumes otherwise
 * waits instead of looking.
 *
 * So it now states the ONE message that exists and denies the rest. Keep it that
 * shape. `SENDING_CHANNELS` in consent.ts is the fact this is written against,
 * and copy-honesty-sweep.test.ts fails on the old wording the moment a channel
 * is added — that guard exists because this sentence, and 22 like it, had no
 * protection at all.
 */
export const NO_NOTIFICATION_NOTE = "One thing reaches you: an email when the attorney's opinion on this request is ready, and " +
    "only if you allowed email on it. Nothing else does — not a change of status, not a meeting " +
    "proposal, not a document. Opening this page is how you find out anything else, and there is " +
    "nothing to switch off.";
/**
 * Statuses a case can actually reach, from which the platform has no way onward.
 *
 * The operator is already told this, plainly, on the ops screen: "Routing and
 * assignment are the only two acts this platform has built, and a case at
 * {status} is past both of them. Everything after assignment — the search, the
 * opinion, packaging and delivery — has no screen yet."
 *
 * The client, who has far more at stake, was told the opposite by implication:
 * CASE_NEXT_STEP.ASSIGNED reads "The attorney does the search at the land
 * registry" in the present tense, which describes work in motion and a state to
 * arrive at. Combined with NO_ESTIMATE_NOTE and no notification, a client three
 * weeks in cannot tell "a registry search takes time" from "this request cannot
 * move". They conclude the first and keep waiting.
 *
 * OPINION_READY is the only entry, and this wall has now moved twice: from
 * ASSIGNED when the attorney's search acts were built, and from UNDER_REVIEW
 * when signing was. A case runs
 * ASSIGNED -> SEARCH_IN_PROGRESS -> UNDER_REVIEW -> OPINION_READY, and the
 * client can read the signed opinion in the app.
 *
 * What has no endpoint now is PACKAGING and DELIVERY — turning the opinion into
 * a document and getting it to the client by some channel. Neither is built:
 * there is no mailer, WhatsApp client or push anywhere in the API, and whether
 * a link may travel over those channels is open question Q4. So a client at
 * OPINION_READY has their opinion and will see nothing further appear.
 *
 * CLOSED is deliberately NOT listed. It has no onward act either, but that is
 * an ending rather than a gap — a client whose request is closed is not waiting
 * for a step that does not exist, and telling them the record stops there would
 * read as a fault where there is none.
 *
 * THIS LIST MUST NOT OUTLIVE THE FEATURE THAT INVALIDATES IT. The moment a
 * lifecycle route past assignment is mounted, this disclosure becomes a false
 * statement of the worst kind — one that tells a client nothing is happening
 * while something is. `apps/api/src/workflow/lifecycle-disclosure.test.ts`
 * asserts it against the routes the API actually mounts, and fails when they
 * change. Do not delete that test to make a build green; update this constant.
 */
export const NO_ONWARD_STEP = ["OPINION_READY"];
/**
 * Said to the client when their case is parked at one of the statuses above.
 * Deliberately not apologetic and not a promise — it states where the record
 * stops and where the work continues, so the reader can act on it.
 */
export const NO_ONWARD_STEP_NOTE = "The attorney's opinion is above, and it is the last thing this platform has to give you on " +
    "this request. If you allowed email on it we have written to say the opinion is ready, but " +
    "that message only points back here — there is no packaged document to come, and this page " +
    "is where the opinion lives. It is the attorney's own, so a question about what it says is " +
    "one for them.";
/** Plain-language subject for each `waitingOn` value. Used as a heading, so short. */
export const WAITING_ON_LABEL = {
    client: "Waiting on you",
    titleverify: "Waiting on TitleVerify",
    attorney: "With the attorney on your case",
    nobody: "Nothing outstanding",
};
//# sourceMappingURL=case-progress.js.map